// =============================================================================
// siteFolderData — everything the Site folder reads, in one place
// (mig 392 + 393, 27 Sep 2026)
// =============================================================================
// Two screens read this: the manager's setup on the project's Site folder tab
// and the crew's folder on the phone. One hook, one set of query keys, so a
// change in setup shows in the crew view without anyone guessing which cache
// to poke.
//
// Rules engine: hse_scope_rules rows carry `requires` = {permits, docs,
// notes}. requiredPermitsFor() unions the permits of the ticked activities —
// that is the crew's minimum set. isHrcw() is true if any ticked activity is
// high-risk construction work.
// =============================================================================

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { isRemoteKm } from "@/features/sites/MedicalLookup";
import { format } from "date-fns";
import { supabase } from "@/lib/supabase";

export const AU_STATES = ["QLD", "NSW", "VIC", "SA", "WA", "TAS", "NT", "ACT"] as const;

export const PERMIT_LABEL: Record<string, string> = {
  hot_work: "Hot work", excavation: "Excavation", workbox: "Workbox", confined_space: "Confined space",
  working_at_heights: "Working at heights", live_electrical: "Live electrical",
};
export const PERMIT_TYPES = Object.keys(PERMIT_LABEL);

export const CONTACT_CATEGORY_LABEL: Record<string, string> = {
  emergency: "Emergency", regulator: "WHS regulator", wildlife: "Wildlife rescue", power: "Powerline emergency",
  environment: "Environment / spills", poisons: "Poisons", ses: "SES", dbyd: "Before you dig", other: "Other",
};

export interface ScopeRule {
  activity_key: string; label: string; hint: string | null;
  requires: { permits?: string[]; docs?: string[]; notes?: string[] };
  is_hrcw: boolean; sort_order: number; is_active: boolean;
}
// An allocation is a SITE SWMS (project_swms_id) or a STANDARD TEMPLATE from
// the library (swms_id) — James, 27 Sep: "should have the standard template
// SWMS as options as well as the custom site SWMS."
export interface SwmsAllocation { project_swms_id?: string | null; swms_id?: string | null; covers: string[] }
export function allocKey(a: { project_swms_id?: string | null; swms_id?: string | null }): string {
  return a.project_swms_id ? `site:${a.project_swms_id}` : `lib:${a.swms_id}`;
}
// One shape for both kinds, so the crew folder and the setup don't care which.
export interface SwmsDoc {
  key: string; kind: "site" | "library"; id: string; number: string; title: string; version: number;
  status: string; usable: boolean; href: string; base_swms_id?: string | null; accepted: any | null;
  // mig 408 — re-induction: an acceptance before reaccept_after no longer counts
  reaccept_after?: string | null; reaccept_reason?: string | null; valid_to?: string | null;
}
export interface HseSetup {
  project_id: string; scope_keys: string[]; swms: SwmsAllocation[]; required_permits: string[];
  hospital_ok: boolean; evac_ok: boolean; contacts_ok: boolean; map_ok: boolean; state_contacts_ok: boolean;
  notes: string | null; signed_off_at: string | null; signed_off_by: string | null; updated_at?: string;
}
export interface StateContact {
  contact_id: string; state: string; category: string; name: string; phone: string; note: string | null;
  verified: boolean; verified_at: string | null; sort_order: number; is_active: boolean;
}

export const EMPTY_SETUP = (projectId: string): HseSetup => ({
  project_id: projectId, scope_keys: [], swms: [], required_permits: [],
  hospital_ok: false, evac_ok: false, contacts_ok: false, map_ok: false, state_contacts_ok: false,
  notes: null, signed_off_at: null, signed_off_by: null,
});

/** James, 27 Sep 2026: "call it out if >100 km as that is a risk". Not stored — computed wherever it's shown. */
export { REMOTE_KM } from "@/features/sites/MedicalLookup";
export function isRemoteSite(site: { nearest_hospital_distance_km?: number | string | null } | null | undefined): boolean {
  return isRemoteKm(site?.nearest_hospital_distance_km);
}
export function todayKey() { return format(new Date(), "yyyy-MM-dd"); }
export function normState(s?: string | null): string | null {
  if (!s) return null;
  const t = s.trim().toUpperCase();
  const map: Record<string, string> = { QUEENSLAND: "QLD", "NEW SOUTH WALES": "NSW", VICTORIA: "VIC", "SOUTH AUSTRALIA": "SA", "WESTERN AUSTRALIA": "WA", TASMANIA: "TAS", "NORTHERN TERRITORY": "NT", "AUSTRALIAN CAPITAL TERRITORY": "ACT" };
  return map[t] ?? (AU_STATES.includes(t as any) ? t : t.slice(0, 3));
}

export function requiredPermitsFor(scopeKeys: string[], rules: ScopeRule[]): string[] {
  const out = new Set<string>();
  for (const r of rules) if (scopeKeys.includes(r.activity_key)) for (const p of r.requires?.permits ?? []) out.add(p);
  return PERMIT_TYPES.filter(p => out.has(p));
}
export function requiredDocsFor(scopeKeys: string[], rules: ScopeRule[]): string[] {
  const out: string[] = [];
  for (const r of rules) if (scopeKeys.includes(r.activity_key)) for (const d of r.requires?.docs ?? []) if (!out.includes(d)) out.push(d);
  return out;
}
export function isHrcw(scopeKeys: string[], rules: ScopeRule[]): boolean {
  return rules.some(r => r.is_hrcw && scopeKeys.includes(r.activity_key));
}
// Which ticked activities have no SWMS covering them.
export function uncoveredActivities(setup: HseSetup): string[] {
  const covered = new Set<string>();
  for (const a of setup.swms ?? []) for (const k of a.covers ?? []) covered.add(k);
  return setup.scope_keys.filter(k => !covered.has(k));
}

// The six setup steps and whether each is done.
export interface StepState { key: string; n: number; label: string; done: boolean; detail: string; blocking: boolean }
export function setupSteps(args: {
  coretel: boolean; setup: HseSetup; rules: ScopeRule[]; site: any; contacts: any[]; siteMap: any; stateContacts: StateContact[];
}): StepState[] {
  const { coretel, setup, rules, site, contacts, siteMap, stateContacts } = args;
  const uncovered = uncoveredActivities(setup);
  const hospital = !!(site?.nearest_hospital_name || site?.nearest_hospital);
  const evac = !!site?.evacuation_point || !!siteMap?.features?.features?.some((f: any) => f.properties?.kind === "assembly");
  const stateOk = stateContacts.length > 0 && stateContacts.filter(c => c.state !== "AU").every(c => c.verified);
  return [
    { key: "system", n: 1, label: "Whose paperwork", done: true, detail: coretel ? "Coretel — done in Ops" : "Client's system", blocking: false },
    { key: "scope", n: 2, label: "Scope of works", done: setup.scope_keys.length > 0,
      detail: setup.scope_keys.length ? `${setup.scope_keys.length} in scope${isHrcw(setup.scope_keys, rules) ? " · HRCW" : ""}` : "Nothing ticked yet", blocking: coretel },
    { key: "swms", n: 3, label: "SWMS", done: setup.scope_keys.length > 0 && (setup.swms?.length ?? 0) > 0 && uncovered.length === 0,
      detail: !setup.swms?.length ? "None allocated" : uncovered.length ? `${uncovered.length} activit${uncovered.length === 1 ? "y" : "ies"} not covered` : `${setup.swms.length} allocated, scope covered`, blocking: coretel },
    { key: "permits", n: 4, label: "Permits required", done: setup.scope_keys.length > 0 && requiredPermitsFor(setup.scope_keys, rules).every(p => setup.required_permits.includes(p)),
      detail: setup.required_permits.length ? setup.required_permits.map(p => PERMIT_LABEL[p] ?? p).join(", ") : "Minimum set not confirmed", blocking: coretel },
    { key: "emergency", n: 5, label: "Emergency & site", done: hospital && (contacts?.length ?? 0) > 0 && stateOk,
      detail: [!hospital && "no nearest hospital", !(contacts?.length) && "no contacts", !evac && "no assembly point", !stateOk && "state contacts unverified"].filter(Boolean).join(" · ") || "Hospital, contacts, state numbers confirmed", blocking: true },
    { key: "signoff", n: 6, label: "Sign off — ready for the crew", done: !!setup.signed_off_at,
      detail: setup.signed_off_at ? `Signed ${new Date(setup.signed_off_at).toLocaleDateString("en-AU", { day: "2-digit", month: "short" })}` : "Manager", blocking: coretel },
  ];
}

// -----------------------------------------------------------------------------
// The hook
// -----------------------------------------------------------------------------
export function useSiteFolder(projectId: string | undefined, opts: { employeeId?: string | null } = {}) {
  const qc = useQueryClient();

  const project = useQuery({
    queryKey: ["site_folder_project", projectId],
    enabled: !!projectId,
    queryFn: async () => {
      const { data, error } = await (supabase as any).from("projects")
        .select(`project_id, project_code, project_name, status, site_id, client_id, source_bid_id,
                 scheduled_start_date, scheduled_end_date, actual_start_date, actual_end_date,
                 hse_system, hse_client_platform, requires_jmp, is_high_risk_construction_work, working_height_m,
                 clients(client_id, legal_name),
                 sites(site_id, site_code, site_name, address_line1, address_line2, suburb, state, postcode, latitude, longitude, what3words,
                       access_details, access_restrictions, parking_notes, key_location, hazards_summary, mobile_coverage,
                       evacuation_point, nearest_hospital, nearest_hospital_name, nearest_hospital_address, nearest_hospital_phone, nearest_hospital_distance_km,
                       nearest_medical_centre_name, nearest_medical_centre_address, nearest_medical_centre_phone, nearest_medical_centre_distance_km)`)
        .eq("project_id", projectId).maybeSingle();
      if (error) throw error;
      return data as any;
    },
  });
  const p = project.data;
  const site = p?.sites ?? null;
  const state = normState(site?.state);
  const coretel = p?.hse_system === "coretel";

  const rules = useQuery({
    queryKey: ["hse_scope_rules"],
    queryFn: async () => {
      const { data } = await (supabase as any).from("hse_scope_rules").select("*").eq("is_active", true).order("sort_order");
      return (data ?? []) as ScopeRule[];
    },
  });

  const setup = useQuery({
    queryKey: ["project_hse_setup", projectId],
    enabled: !!projectId,
    queryFn: async () => {
      const { data } = await (supabase as any).from("project_hse_setup").select("*").eq("project_id", projectId).maybeSingle();
      return (data ?? EMPTY_SETUP(projectId!)) as HseSetup;
    },
  });

  const stateContacts = useQuery({
    queryKey: ["hse_state_contacts", state],
    enabled: !!p,
    queryFn: async () => {
      const { data } = await (supabase as any).from("hse_state_contacts").select("*")
        .in("state", state ? ["AU", state] : ["AU"]).eq("is_active", true).order("sort_order");
      return (data ?? []) as StateContact[];
    },
  });

  const contacts = useQuery({
    queryKey: ["site_folder_contacts", projectId],
    enabled: !!projectId,
    queryFn: async () => {
      const { data } = await (supabase as any).schema("app").rpc("project_contacts", { p_project_id: projectId });
      return (data ?? []) as { contact_role: string; full_name: string; mobile: string | null }[];
    },
  });

  const clientContacts = useQuery({
    queryKey: ["site_folder_client_contacts", p?.client_id, state],
    enabled: !!p?.client_id,
    queryFn: async () => {
      const { data } = await (supabase as any).from("client_contacts")
        .select("contact_id, full_name, role_title, phone, email, state, is_field_manager")
        .eq("client_id", p.client_id).eq("is_active", true).eq("is_field_manager", true);   // mig 394 — the crew is meant to call the field manager, nobody else on the client list
      return ((data ?? []) as any[]).filter(c => !c.state || !state || normState(c.state) === state);
    },
  });

  const siteMap = useQuery({
    queryKey: ["site_folder_map", projectId, p?.source_bid_id],
    enabled: !!p,
    queryFn: async () => {
      let q = (supabase as any).from("site_maps")
        .select("site_map_id, map_number, title, centre_lat, centre_lng, features, version, is_locked, locked_at, updated_at")
        .eq("is_active", true);
      q = p.source_bid_id ? q.or(`project_id.eq.${projectId},bid_id.eq.${p.source_bid_id}`) : q.eq("project_id", projectId);
      const { data } = await q.order("is_locked", { ascending: false }).order("updated_at", { ascending: false }).limit(1);
      return (data?.[0] ?? null) as any;
    },
  });

  // Site SWMS for this job + the standard templates from the library, in one
  // list, each with whether THIS person has accepted the current version.
  const swmsList = useQuery({
    queryKey: ["site_folder_swms", projectId, opts.employeeId ?? null],
    enabled: !!projectId,
    queryFn: async (): Promise<SwmsDoc[]> => {
      const [{ data: s }, { data: lib }, { data: a }] = await Promise.all([
        (supabase as any).from("project_swms")
          .select("project_swms_id, swms_number, title, version, status, approved_at, base_swms_id, reaccept_after, reaccept_reason, valid_to")
          .eq("project_id", projectId).order("approved_at", { ascending: false, nullsFirst: false }),
        (supabase as any).from("swms_library")
          .select("swms_id, swms_code, display_name, scope_of_work, is_active, content_status, content_version, is_high_risk_construction_work")
          .eq("is_active", true).order("swms_code"),
        opts.employeeId
          ? (supabase as any).from("swms_acceptances").select("project_swms_id, swms_id, swms_version, accepted_at").eq("employee_id", opts.employeeId)
          : Promise.resolve({ data: [] }),
      ]);
      const mine = new Map<string, any>();
      for (const x of (a ?? []) as any[]) {
        if (x.project_swms_id) mine.set(`site:${x.project_swms_id}:${x.swms_version}`, x);
        else if (x.swms_id) mine.set(`lib:${x.swms_id}:${x.swms_version}`, x);
      }
      const site: SwmsDoc[] = ((s ?? []) as any[]).map(r => ({
        key: `site:${r.project_swms_id}`, kind: "site", id: r.project_swms_id, number: r.swms_number, title: r.title, version: r.version ?? 1,
        status: r.status, usable: r.status === "approved", href: `/hse/swms/site/${r.project_swms_id}`, base_swms_id: r.base_swms_id,
        // mig 408 — a director can require re-acceptance after an incident: only an acceptance AFTER that moment counts
        accepted: (() => { const a = mine.get(`site:${r.project_swms_id}:${r.version ?? 1}`) ?? null; return a && r.reaccept_after && a.accepted_at <= r.reaccept_after ? null : a; })(),
        reaccept_after: r.reaccept_after ?? null, reaccept_reason: r.reaccept_reason ?? null, valid_to: r.valid_to ?? null,
      }));
      const library: SwmsDoc[] = ((lib ?? []) as any[]).map(r => ({
        key: `lib:${r.swms_id}`, kind: "library", id: r.swms_id, number: r.swms_code, title: r.display_name, version: r.content_version ?? 1,
        status: r.content_status === "approved" ? "approved" : r.content_status === "draft" ? "draft" : "standard",
        usable: r.content_status !== "draft", href: `/hse/swms/${r.swms_id}`, base_swms_id: null,
        accepted: mine.get(`lib:${r.swms_id}:${r.content_version ?? 1}`) ?? null,
      }));
      return [...site, ...library];
    },
  });

  const todayCards = useQuery({
    queryKey: ["site_folder_cards", projectId, todayKey()],
    enabled: !!projectId,
    queryFn: async () => {
      const { data } = await (supabase as any).from("hse_start_cards")
        .select("card_id, card_number, status, work_area, submitted_at, created_at, first_aider_name, first_aider_contact, supervisor_phone")
        .eq("project_id", projectId).eq("card_date", todayKey()).order("created_at", { ascending: false });
      return (data ?? []) as any[];
    },
  });
  const cardIds = (todayCards.data ?? []).map((c: any) => c.card_id);
  const signons = useQuery({
    queryKey: ["site_folder_signons", cardIds.join(",")],
    enabled: cardIds.length > 0,
    queryFn: async () => {
      const { data } = await (supabase as any).from("hse_start_card_signons")
        .select("signon_id, card_id, name, employee_id, signed_at").in("card_id", cardIds).order("signed_at");
      return (data ?? []) as any[];
    },
  });

  const permits = useQuery({
    queryKey: ["site_folder_permits", projectId],
    enabled: !!projectId,
    queryFn: async () => {
      const { data } = await (supabase as any).from("permits")
        .select("permit_id, permit_number, permit_type, status, scope_description, valid_from, valid_until, location_on_site, permit_acks(ack_id, name)")
        .eq("project_id", projectId).not("status", "in", "(closed,cancelled)").order("valid_until");
      return (data ?? []) as any[];
    },
  });

  const incidents = useQuery({
    queryKey: ["site_folder_incidents", projectId],
    enabled: !!projectId,
    queryFn: async () => {
      const { data } = await (supabase as any).from("incidents")
        .select("incident_id, incident_number, severity, short_description, occurred_at, status")
        .eq("project_id", projectId).order("occurred_at", { ascending: false }).limit(5);
      return (data ?? []) as any[];
    },
  });

  function invalidateAll() {
    for (const k of ["site_folder_project", "project_hse_setup", "hse_state_contacts", "site_folder_contacts", "site_folder_client_contacts",
                     "site_folder_map", "site_folder_swms", "site_folder_cards", "site_folder_signons", "site_folder_permits", "site_folder_incidents", "project"]) {
      qc.invalidateQueries({ queryKey: [k] });
    }
  }

  return {
    project: p, site, state, coretel,
    setup: setup.data ?? (projectId ? EMPTY_SETUP(projectId) : null),
    rules: rules.data ?? [], stateContacts: stateContacts.data ?? [], contacts: contacts.data ?? [], clientContacts: clientContacts.data ?? [],
    siteMap: siteMap.data ?? null, swmsList: swmsList.data ?? [], todayCards: todayCards.data ?? [], signons: signons.data ?? [],
    permits: permits.data ?? [], incidents: incidents.data ?? [],
    isLoading: project.isLoading, invalidateAll,
  };
}
